import React from 'react';
import { useTranslation } from 'react-i18next';
import type { BillingTransaction } from '@hero/schema';
import { cn } from '@hero/web-lib';
import { ChevronDownIcon, RefreshCcwIcon } from '@hero/web-icons';
import { BillingTransactionsList } from '../BillingTransactionsList';

export interface BillingPaymentHistoryProps {
  transactions: BillingTransaction[];
  isLoading?: boolean;
  /**
   * Force-open the section. By default the section opens automatically
   * when there's at least one `failed` attempt (so users see what went
   * wrong) and stays collapsed otherwise.
   */
  defaultOpen?: boolean;
  className?: string;
}

/**
 * Collapsible "Payment history" section shown beneath the items / summary
 * grid on `BillingDetailPage`.
 *
 * Uses a native `<details>` element so we get keyboard + accessibility
 * for free without a runtime dependency on an Accordion component.
 * The summary chevron flips via `[&[open]>summary>svg]` so we don't
 * need extra state.
 */
export function BillingPaymentHistory({
  transactions,
  isLoading,
  defaultOpen,
  className,
}: BillingPaymentHistoryProps) {
  const { t } = useTranslation('m-billing');
  const hasFailedAttempt = transactions.some(tx => tx.status === 'failed');
  const open = defaultOpen ?? hasFailedAttempt;

  return (
    <details
      open={open}
      className={cn(
        'group flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm',
        className,
      )}>
      <summary className="flex cursor-pointer items-center justify-between gap-3 list-none [&::-webkit-details-marker]:hidden">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <RefreshCcwIcon className="size-5" />
          </div>
          <div className="flex flex-col gap-0.5">
            <h3 className="text-base font-semibold text-gray-900">
              {t('billing_doc.detail.payment_history_title')}
            </h3>
            <p className="text-sm text-gray-500">
              {t('billing_doc.detail.payment_history_description')}
            </p>
          </div>
        </div>
        <ChevronDownIcon className="size-4 text-gray-500 transition-transform group-open:rotate-180" />
      </summary>

      <div className="mt-1">
        {transactions.length === 0 && !isLoading ? (
          <p className="rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-4 py-6 text-center text-sm text-gray-500">
            {t('billing_doc.detail.payment_history_empty')}
          </p>
        ) : (
          <BillingTransactionsList
            transactions={transactions}
            isLoading={isLoading}
          />
        )}
      </div>
    </details>
  );
}
